import { useNavigate } from "react-router-dom";
import BarcodeScanner from "./BarcodeScanner";
import { toast } from "sonner";

/**
 * QRScanner — thin wrapper around BarcodeScanner that adds:
 *  - beep + vibration feedback on successful scan (with graceful fallback if
 *    the browser does not expose the Vibration API — desktop, iOS Safari, etc.);
 *  - smart URL parsing: any QR whose content ends with `/q/<token>` triggers
 *    an in-app navigation to `/q/<token>` (the QRRedirect route handles auth
 *    routing). Any other content is echoed via toast so operators can still
 *    read arbitrary QRs (product labels, shipping barcodes, etc.).
 */
export default function QRScanner({ onClose, onDetected }) {
  const navigate = useNavigate();

  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
    } catch { /* ignore — silent on unsupported browsers */ }
    // Vibration is best-effort: absent on iOS Safari and most desktops.
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(100);
      }
    } catch { /* ignore */ }
  };

  const handleDetected = ({ text }) => {
    beep();
    // Parse "/q/<token>" from any URL-shaped payload
    let token = null;
    try {
      const trimmed = (text || "").trim();
      if (trimmed) {
        const url = new URL(trimmed, window.location.origin);
        const m = url.pathname.match(/\/q\/([A-Za-z0-9_-]{16,})/);
        if (m) token = m[1];
      }
    } catch {
      // Fallback: raw string might be just the token itself
      const raw = (text || "").trim();
      if (/^[A-Za-z0-9_-]{16,}$/.test(raw)) token = raw;
    }
    if (token) {
      toast.success("QR pratica riconosciuto");
      onClose?.();
      navigate(`/q/${token}`);
      return;
    }
    // Non-pratica QR: bubble up if a custom handler was provided, else show it.
    if (onDetected) {
      onDetected({ text });
      onClose?.();
    } else {
      toast.info(`Codice letto: ${text}`);
    }
  };

  return (
    <BarcodeScanner
      onDetected={handleDetected}
      onManualEntry={(t) => handleDetected({ text: t })}
      onClose={onClose}
    />
  );
}
